/**
 * Auto-replace text shortcuts — the single source of truth.
 *
 * "Type these characters, get this glyph." Add new entries here
 * (e.g. { trigger: "=>", replace: "⇒" }) or swap `replace` values to ASCII
 * output — no consumer logic changes. Everything downstream (the TipTap
 * extension, and any plain-textarea hook) is generated from this list.
 */
export interface TextShortcut {
  /** The literal characters the user types. */
  trigger: string;
  /** What the trigger is replaced with. */
  replace: string;
}

export const TEXT_SHORTCUTS: TextShortcut[] = [
  { trigger: "<->", replace: "↔" },
  { trigger: "->", replace: "→" }
  // Future examples — enable by uncommenting:
  // { trigger: "=>", replace: "⇒" },
  // { trigger: "<=", replace: "⇐" },
];

/** Escape a literal string so it can be embedded safely in a RegExp. */
const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export interface CompiledShortcut extends TextShortcut {
  /** Matches the trigger anchored to the end of the text before the caret. */
  find: RegExp;
}

/**
 * Compile shortcuts into caret-anchored regexes, longest trigger first.
 *
 * Ordering is critical: a trigger that is a superstring of another
 * (e.g. "<->" contains "->") MUST be tried first, otherwise typing the
 * completing ">" of "<->" would match "->" and produce "<→". Consumers must
 * apply the rules in the returned order.
 */
export const compileShortcuts = (
  shortcuts: TextShortcut[] = TEXT_SHORTCUTS
): CompiledShortcut[] =>
  [...shortcuts]
    .sort((a, b) => b.trigger.length - a.trigger.length)
    .map((shortcut) => ({
      ...shortcut,
      find: new RegExp(`${escapeRegExp(shortcut.trigger)}$`, "u")
    }));
